import { useEffect } from "react";
import type { ChannelMessage } from "@atrium/shared";
import { getSocket } from "../../lib/socket";
import { queryClient } from "../../lib/queryClient";
import { patchMessage, removeMessage, upsertMessage } from "../chat/cache";
import { threadKey, threadMessagesKey, threadsKey } from "./hooks";

/** Cached post message lists of a channel: ["messages", channelId, threadId]. */
const threadKeys = (channelId: string) =>
  queryClient
    .getQueriesData({ queryKey: ["messages", channelId] })
    .map(([k]) => k as unknown[])
    .filter((k) => k.length === 3);

/**
 * Keeps forum caches live: messages inside posts go to their post's list (not the
 * channel's), and post lists refresh on thread:update / thread:delete. Mounted once,
 * from RealtimeBridge.
 */
export function useForumRealtime(meId: string) {
  useEffect(() => {
    const s = getSocket();
    if (!s) return;
    const onNew = (m: ChannelMessage) => m.threadId && upsertMessage(threadMessagesKey(m.channelId, m.threadId), m);
    const onUpdate = (m: ChannelMessage) => m.threadId && patchMessage<ChannelMessage>(threadMessagesKey(m.channelId, m.threadId), m.id, () => m);
    const onDelete = (p: { id: string; channelId: string }) => threadKeys(p.channelId).forEach((k) => removeMessage(k, p.id));
    const onBulkDelete = (p: { ids: string[]; channelId: string }) => threadKeys(p.channelId).forEach((k) => p.ids.forEach((id) => removeMessage(k, id)));
    const onReactions = (p: { id: string; channelId: string; reactions: { emoji: string; count: number }[]; userId: string; emoji: string; added: boolean }) =>
      threadKeys(p.channelId).forEach((k) =>
        patchMessage<ChannelMessage>(k, p.id, (m) => {
          const mine = new Set(m.reactions.filter((r) => r.me).map((r) => r.emoji));
          if (p.userId === meId) {
            if (p.added) mine.add(p.emoji);
            else mine.delete(p.emoji);
          }
          return { ...m, reactions: p.reactions.map((r) => ({ ...r, me: mine.has(r.emoji) })) };
        }),
      );
    const onThreadUpdate = (p: { channelId: string; threadId: string }) => {
      void queryClient.invalidateQueries({ queryKey: threadsKey(p.channelId) });
      void queryClient.invalidateQueries({ queryKey: threadKey(p.threadId) });
    };
    const onThreadDelete = (p: { channelId: string; threadId: string }) => {
      void queryClient.invalidateQueries({ queryKey: threadsKey(p.channelId) });
      void queryClient.invalidateQueries({ queryKey: threadKey(p.threadId) });
      queryClient.removeQueries({ queryKey: threadMessagesKey(p.channelId, p.threadId) });
    };
    s.on("message:new", onNew);
    s.on("message:update", onUpdate);
    s.on("message:delete", onDelete);
    s.on("message:bulk-delete", onBulkDelete);
    s.on("message:reactions", onReactions);
    s.on("thread:update", onThreadUpdate);
    s.on("thread:delete", onThreadDelete);
    return () => {
      s.off("message:new", onNew);
      s.off("message:update", onUpdate);
      s.off("message:delete", onDelete);
      s.off("message:bulk-delete", onBulkDelete);
      s.off("message:reactions", onReactions);
      s.off("thread:update", onThreadUpdate);
      s.off("thread:delete", onThreadDelete);
    };
  }, [meId]);
}
